/* eslint-disable react/no-danger */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ReplayIcon from '@material-ui/icons/Replay';
import RadarDiagram from '@orientaction/components/Beneficiary/Result/Radar';
import { isAdmin } from '@orientaction/utils';
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import clsx from 'clsx';
import { kebabCase } from 'lodash';
import ouibounce from 'ouibounce';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSnackbar } from 'react-simple-snackbar';
import useFeedbackSubmit from '../../../../../hooks/useFeedbackSubmit';
import HelpModal from '../../../Auth/components/HelpModal';
import {
  Breadcrumbs,
  FeedbackModal,
  MediaVideoPlayer,
  RedesignLayout,
  useSharedStyles,
} from '../../../shared';
import RedesignImage from '../../../shared/components/RedesignImage';
import { redesignTheme } from '../../../theme';
import {
  getStaffResultBackPath,
  isStaffViewer,
  readBeneficiaryId,
} from '../../shared/adminResultNav';
import IASynthesisBlock from './components/IASynthesisBlock';
import RecommendedTestsSection from './components/RecommendedTestsSection';
import ResultDiagramSection from './components/ResultDiagramSection';
import ResultHistoryDropdown from './components/ResultHistoryDropdown';
import ResultShareBlock from './components/ResultShareBlock';
import ResultTextBlock from './components/ResultTextBlock';
import useMinorTestResultPage from './hooks/useMinorTestResultPage';
import {
  MOBILE_SNACKBAR_TOP,
  useMinorTestResultSnackbarStyles,
  useMinorTestResultStyles,
} from './index.styles';

Chart.register(ChartDataLabels);

const MinorTestResults = () => {
  const classes = useMinorTestResultStyles();
  const sharedClasses = useSharedStyles();
  useMinorTestResultSnackbarStyles();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    loadPage,
    user,
    dataParams,
    test,
    userResponse,
    variables,
    completedTestIds,
    aglomerationsTraited,
    dataMax,
    sendDataMax,
    canShowResult,
    isSimplePie,
    isRadarDiagram,
    diagramId,
    titrePage,
    titreGraphique,
    texteResultat,
    historyItems,
    recommendedTests,
    recommendedTestsOrder,
    resultId,
  } = useMinorTestResultPage();

  const isBeneficiary = !isAdmin(user);
  const staffViewer = isStaffViewer(user);
  const beneficiaryId = readBeneficiaryId(location, dataParams.idUser);

  const handleBackToBeneficiaryTests = () => {
    history.push(getStaffResultBackPath(location, beneficiaryId));
  };

  const [helpOpen, setHelpOpen] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [expandedIds, setExpandedIds] = useState<number[]>([]);

  const { submitFeedback, isSubmitting } = useFeedbackSubmit(test?.id?.toString() || '');

  const [openSnackbar] = useSnackbar({
    position: isMobile ? 'top-center' : 'bottom-right',
    style: {
      position: 'fixed',
      backgroundColor: redesignTheme.colors.primaryDark,
      color: redesignTheme.colors.white,
      fontFamily: redesignTheme.fonts.opensans,
      fontSize: redesignTheme.fontSize.base,
      borderRadius: redesignTheme.radius.sm,
      padding: '8px',
      minWidth: '400px',
      minHeight: '48px',
      display: 'flex',
      alignItems: 'center',
      ...(isMobile
        ? {
            top: MOBILE_SNACKBAR_TOP,
            left: '50%',
            transform: 'translateX(-50%)',
            right: 'auto',
            bottom: 'auto',
          }
        : {
            bottom: '24px',
            right: '24px',
            left: 'auto',
            top: 'auto',
            transform: 'none',
          }),
      margin: 0,
    },
  });

  useEffect(() => {
    if (!isBeneficiary || loadPage || hasTriggered) return;

    const timer = setTimeout(() => {
      if (!hasTriggered) {
        setShowFeedbackModal(true);
        setHasTriggered(true);
      }
    }, 20000);

    let ouibounceInstance: any = null;
    if (!isMobile) {
      ouibounceInstance = ouibounce(false, {
        callback: () => {
          if (!hasTriggered) {
            setShowFeedbackModal(true);
            setHasTriggered(true);
            clearTimeout(timer);
          }
        },
        aggressive: true,
        sensitivity: 20,
        delay: 1000,
      });
    }

    return () => {
      clearTimeout(timer);
      if (ouibounceInstance?.disable) {
        ouibounceInstance.disable();
      }
    };
  }, [isBeneficiary, loadPage, hasTriggered, isMobile]);

  const testUrl = useMemo(() => {
    if (!test?.id) return '';
    return `${process.env.REACT_APP_SHARE_ENDPOINT}/api/test/${test.id}/${kebabCase(
      test?.attributes?.title
    )}`;
  }, [test]);

  const resultTitleHtml = dataMax?.title || titrePage;
  const testLabel = test?.attributes?.title || '';
  const hasImage = Boolean(dataMax?.image);
  const hasVideo = Boolean(dataMax?.video);

  const handleRetakeTest = () => {
    if (!test?.id) return;
    history.push(`/test/${test.id}/${kebabCase(test.attributes.title)}`);
  };

  const handleFeedbackSubmit = async (rating: number, comment: string) => {
    const success = await submitFeedback(rating, comment);
    if (success) {
      setShowFeedbackModal(false);
      openSnackbar('Votre avis a bien été pris en compte');
    }
  };

  const handleAccordionChange = (id: number) => (_event: any, isExpanded: boolean) => {
    setExpandedIds(prev =>
      isExpanded ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter(x => x !== id)
    );
  };

  const hiddenDiagram = !canShowResult && !isRadarDiagram && (
    <div className={classes.hiddenDiagram}>
      <ResultDiagramSection
        userResponse={userResponse}
        variables={variables}
        diagramId={diagramId}
        isSimplePie={isSimplePie}
        sendDataMax={sendDataMax}
      />
    </div>
  );

  return (
    <RedesignLayout isaddBackground={false} footer={true}>
      {loadPage ? (
        <div className={sharedClasses.loadingCenter}>
          <CircularProgress style={{ color: redesignTheme.colors.primaryDark }} />
        </div>
      ) : (
        <main className={clsx(sharedClasses.pageShell, classes.page)}>
          <div className={classes.contentContainer}>
            <div className={classes.headerSection}>
              {staffViewer ? (
                <button
                  type="button"
                  className={classes.backButton}
                  onClick={handleBackToBeneficiaryTests}>
                  ← Retour
                </button>
              ) : (
                <Breadcrumbs
                  items={[{ label: 'Accueil', href: '/main' }, { label: 'Résultat du test' }]}
                />
              )}
              {canShowResult && (
                <div className={classes.historyAnchor}>
                  <ResultHistoryDropdown
                    testResults={historyItems}
                    currentResultId={resultId}
                    redirectPath="/mes-resultats"
                    idUser={dataParams.idUser}
                  />
                </div>
              )}
            </div>

            {!canShowResult && !isRadarDiagram ? (
              <>
                {hiddenDiagram}
                <div className={classes.invalidState}>
                  <div>
                    <h1 className={classes.invalidTitle}>
                      Vos résultats de test ne peuvent pas s&apos;afficher correctement
                    </h1>
                    <p className={classes.invalidDescription}>
                      Nous vous invitons à revenir vers nous.
                    </p>
                    <button
                      type="button"
                      className={classes.retakeButton}
                      onClick={() => setHelpOpen(true)}>
                      Contactez-nous, nous pouvons vous aider
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {!isRadarDiagram && dataMax && (
                  <div className={classes.mainGrid}>
                    <p className={classes.resultEyebrow}>
                      Résultat du test{testLabel ? ` : ${testLabel}` : ''}
                    </p>
                    <div className={classes.leftColumn}>
                      <div
                        className={classes.resultTitle}
                        role="heading"
                        aria-level={1}
                        dangerouslySetInnerHTML={{ __html: resultTitleHtml }}
                      />

                      <div className={classes.mediaStack}>
                        {hasImage && diagramId !== 1 && (
                          <RedesignImage
                            src={dataMax.image}
                            alt="Illustration du résultat"
                            className={classes.resultImage}
                          />
                        )}
                        {hasVideo && (
                          <div className={classes.videoWrap}>
                            <MediaVideoPlayer videoUrl={dataMax.video} />
                          </div>
                        )}
                      </div>

                      {isBeneficiary && (
                        <button
                          type="button"
                          className={classes.retakeButton}
                          onClick={handleRetakeTest}>
                          <ReplayIcon className={classes.retakeIcon} />
                          Refaire le test
                        </button>
                      )}

                      <ResultShareBlock
                        testUrl={testUrl}
                        shareTitle={dataMax?.title}
                        onHelpClick={() => setHelpOpen(true)}
                      />
                    </div>

                    <div className={classes.rightColumn}>
                      <ResultDiagramSection
                        userResponse={userResponse}
                        variables={variables}
                        diagramId={diagramId}
                        isSimplePie={isSimplePie}
                        sendDataMax={sendDataMax}
                        diagramTitle={titreGraphique || 'Explorez les détails de votre résultat'}
                      />
                      <ResultTextBlock html={texteResultat} />
                    </div>
                  </div>
                )}

                {isRadarDiagram && (
                  <div>
                    <div className={classes.leftColumn} style={{ maxWidth: '100%' }}>
                      <p className={classes.resultEyebrow}>Résultat du test</p>
                      <h1 className={classes.resultTitle}>{titrePage}</h1>
                    </div>

                    <Grid container={true} spacing={4} style={{ marginTop: 24 }}>
                      <Grid item={true} xs={12} lg={5}>
                        <Typography style={{ fontFamily: redesignTheme.fonts.opensansBold, marginBottom: 16 }}>
                          Explorez les détails de vos résultats
                        </Typography>
                        <Typography
                          style={{ fontFamily: redesignTheme.fonts.opensansBold, fontSize: 14, marginBottom: 13 }}>
                          Vos scores
                        </Typography>
                        {aglomerationsTraited.map((agl: any) => (
                          <div key={agl.id} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                            <span style={{ color: redesignTheme.colors.primary, width: 30 }}>
                              {agl.totalVariableValue}
                            </span>
                            <span>{agl.attributes.name}</span>
                          </div>
                        ))}
                      </Grid>
                      <Grid item={true} xs={12} lg={7}>
                        <Typography
                          align="center"
                          style={{ fontFamily: redesignTheme.fonts.opensansBold, marginBottom: 16 }}>
                          {titreGraphique || 'Titre du graphique'}
                        </Typography>
                        <RadarDiagram
                          data={aglomerationsTraited}
                          min={test?.attributes?.minValueAg}
                          max={test?.attributes?.maxValueAg}
                        />
                      </Grid>
                    </Grid>

                    <div style={{ marginTop: 24 }}>
                      {aglomerationsTraited.map((el: any) => (
                        <Accordion
                          key={el.id}
                          expanded={expandedIds.includes(el.id)}
                          onChange={handleAccordionChange(el.id)}
                          elevation={0}
                          style={{
                            marginTop: 8,
                            border: expandedIds.includes(el.id)
                              ? 'none'
                              : `1px solid ${redesignTheme.colors.primary}`,
                          }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography style={{ fontFamily: redesignTheme.fonts.opensansSemiBold }}>
                              {el.attributes.name}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid container={true} spacing={3}>
                              <Grid item={true} xs={12} md={6}>
                                <Typography
                                  className={classes.resultEyebrow}
                                  style={{ marginBottom: 8 }}>
                                  Résultat du test
                                </Typography>
                                <Typography
                                  className={classes.resultTitle}
                                  style={{ fontSize: redesignTheme.fontSize.lg, marginBottom: 16 }}
                                  dangerouslySetInnerHTML={{
                                    __html: el?.matchedVariable?.attributes?.title,
                                  }}
                                />
                                {el?.matchedVariable?.attributes?.image && (
                                  <RedesignImage
                                    src={el.matchedVariable.attributes.image}
                                    alt="Illustration résultat"
                                    className={classes.resultImage}
                                  />
                                )}
                                {el?.matchedVariable?.attributes?.video && (
                                  <div className={classes.videoWrap} style={{ marginTop: 12 }}>
                                    <MediaVideoPlayer videoUrl={el.matchedVariable.attributes.video} />
                                  </div>
                                )}
                              </Grid>
                              <Grid item={true} xs={12} md={6}>
                                <ResultTextBlock
                                  html={el?.matchedVariable?.attributes?.description || ''}
                                />
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </div>

                    {aglomerationsTraited.some((el: any) => el?.matchedVariable) && (
                      <div style={{ marginTop: 24 }}>
                        {isBeneficiary && (
                          <button
                            type="button"
                            className={classes.retakeButton}
                            onClick={handleRetakeTest}>
                            <ReplayIcon className={classes.retakeIcon} />
                            Refaire le test
                          </button>
                        )}
                        <ResultShareBlock
                          testUrl={testUrl}
                          shareTitle={titrePage}
                          onHelpClick={() => setHelpOpen(true)}
                        />
                      </div>
                    )}
                  </div>
                )}

                {isBeneficiary && !isRadarDiagram && userResponse && (
                  <div className={classes.aiSection}>
                    <IASynthesisBlock
                      userId={user.id}
                      responseTestId={dataParams.id}
                      testName={test?.attributes?.title || 'Test'}
                    />
                  </div>
                )}

                <RecommendedTestsSection
                  recommendedTests={recommendedTests}
                  completedTestIds={completedTestIds}
                  recommendedTestsOrder={recommendedTestsOrder}
                />
              </>
            )}
          </div>
        </main>
      )}

      <HelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        defaultEmail={user?.email}
        defaultName={user?.lastname || user?.lastName || ''}
        defaultFirstName={user?.firstname || user?.firstName || ''}
      />

      {isBeneficiary && (
        <FeedbackModal
          open={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={handleFeedbackSubmit}
          isSubmitting={isSubmitting}
        />
      )}
    </RedesignLayout>
  );
};

export default MinorTestResults;
